import xss from "xss";

// XSS sanitization options
const xssOptions = {
  whiteList: {}, // Allow no HTML tags
  stripIgnoreTag: true, // Remove unrecognized tags
  stripIgnoreTagBody: ["script", "style"], // Remove script and style content
};

// Sanitize a single string value
export const sanitize = (input: string | undefined | null): string => {
  if (!input) return "";
  return xss(String(input).trim(), xssOptions);
};

// Sanitize email (trim, lowercase, basic XSS)
export const sanitizeEmail = (email: string | undefined | null): string => {
  if (!email) return "";
  return xss(String(email).trim().toLowerCase(), xssOptions);
};

// Sanitize phone (keep only digits, +, -, spaces, parentheses)
export const sanitizePhone = (phone: string | undefined | null): string => {
  if (!phone) return "";
  // First remove any XSS attempts
  const cleaned = xss(String(phone), xssOptions);
  // Keep only valid phone characters
  return cleaned.replace(/[^\d\+\-\s\(\)]/g, "").trim();
};

// Recursively sanitize an object's string values
export const sanitizeObject = <T extends Record<string, unknown>>(
  obj: T
): T => {
  const result = { ...obj };

  for (const key in result) {
    const value = result[key];

    if (typeof value === "string") {
      // Special handling for known field types
      if (key === "email") {
        (result as Record<string, unknown>)[key] = sanitizeEmail(value);
      } else if (key === "phone") {
        (result as Record<string, unknown>)[key] = sanitizePhone(value);
      } else {
        (result as Record<string, unknown>)[key] = sanitize(value);
      }
    } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      (result as Record<string, unknown>)[key] = sanitizeObject(
        value as Record<string, unknown>
      );
    }
    // Booleans, numbers, and arrays pass through unchanged
  }

  return result;
};

// Sanitize form data before submission
export const sanitizeFormData = <T extends Record<string, unknown>>(
  data: T
): T => {
  return sanitizeObject(data);
};
